import { useState, useMemo, useCallback } from "react";
import {
  ExternalLink,
  SlidersHorizontal,
  Check,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { Link, useParams } from "@tanstack/react-router";
import * as Popover from "@radix-ui/react-popover";
import * as Checkbox from "@radix-ui/react-checkbox";
import { useTranslation } from "@/packages/utils";
import { expiry } from "./dashboard.data";
import type { ExpiryLevel } from "./dashboard.types";

function urgencyDotColor(days: number): string {
  if (days <= 14) return "bg-red-500";
  if (days <= 45) return "bg-amber-400";
  return "bg-emerald-500";
}

function urgencyTextColor(days: number): string {
  if (days <= 14) return "text-red-600";
  if (days <= 45) return "text-amber-600";
  return "text-emerald-600";
}

function getUrgencyLevel(days: number): ExpiryLevel {
  if (days <= 14) return "red";
  if (days <= 45) return "yellow";
  return "green";
}

const URGENCY_OPTIONS: {
  level: ExpiryLevel;
  dotClass: string;
  label: string;
}[] = [
  { level: "red", dotClass: "bg-red-500", label: "Kritisch (\u226414 Tage)" },
  {
    level: "yellow",
    dotClass: "bg-amber-400",
    label: "Bald f\u00e4llig (\u226445 Tage)",
  },
  {
    level: "green",
    dotClass: "bg-emerald-500",
    label: "Im Zeitplan (>45 Tage)",
  },
];

function ExpandedExpiryDetail({
  item,
}: {
  item: { version: string; tags: string[]; documentId?: string };
}) {
  const { workspaceSlug } = useParams({ strict: false });

  return (
    <div className="mt-0.5 rounded bg-surface/80 px-2.5 py-2 text-[11px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {item.version}
          </span>
          {item.tags.map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded bg-surface text-secondary"
            >
              {tag}
            </span>
          ))}
        </div>
        {item.documentId && workspaceSlug && (
          <Link
            to="/$workspaceSlug/documents/$documentId"
            params={{
              workspaceSlug: workspaceSlug as string,
              documentId: item.documentId,
            }}
            className="text-accent hover:text-accent/80 transition-colors shrink-0"
            title="Open document"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

export function ExpiryWarnings() {
  const { t } = useTranslation(["dashboard"]);

  const [urgencyFilter, setUrgencyFilter] = useState<Set<ExpiryLevel>>(
    () => new Set<ExpiryLevel>(["red", "yellow", "green"]),
  );
  const [sortAsc, setSortAsc] = useState(true);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [expandedSet, setExpandedSet] = useState<Set<number>>(() => new Set());

  const toggleExpand = useCallback((index: number) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    return expiry
      .filter((item) => urgencyFilter.has(getUrgencyLevel(item.days)))
      .sort((a, b) => (sortAsc ? a.days - b.days : b.days - a.days))
      .slice(0, 6);
  }, [urgencyFilter, sortAsc]);

  const handleToggleUrgency = (level: ExpiryLevel, checked: boolean) => {
    setUrgencyFilter((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(level);
      } else if (next.size > 1) {
        next.delete(level);
      }
      return next;
    });
  };

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center px-4 pt-3 pb-1 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:expiryWarnings.title")}
        </h3>
        <div className="ml-auto">
          <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-surface transition-colors"
                title="Filter"
              >
                <SlidersHorizontal size={14} />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                side="bottom"
                align="end"
                sideOffset={8}
                className="z-50 w-56 rounded-xl border border-border bg-card p-3 shadow-lg"
              >
                <p className="text-xs font-semibold text-secondary mb-2">
                  Dringlichkeit
                </p>
                <div className="flex flex-col gap-1.5">
                  {URGENCY_OPTIONS.map(({ level, dotClass, label }) => {
                    const isChecked = urgencyFilter.has(level);
                    const isLastChecked = isChecked && urgencyFilter.size === 1;

                    return (
                      <label
                        key={level}
                        htmlFor={`urgency-filter-${level}`}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                          isLastChecked
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:bg-surface"
                        }`}
                      >
                        <Checkbox.Root
                          id={`urgency-filter-${level}`}
                          checked={isChecked}
                          disabled={isLastChecked}
                          onCheckedChange={(checked) =>
                            handleToggleUrgency(level, checked === true)
                          }
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface transition-colors data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                        >
                          <Checkbox.Indicator>
                            <Check size={12} className="text-white" />
                          </Checkbox.Indicator>
                        </Checkbox.Root>
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`}
                        />
                        <span className="text-primary text-sm">{label}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-3 pt-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setSortAsc((prev) => !prev)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-primary hover:bg-surface transition-colors cursor-pointer"
                  >
                    <ArrowUpDown
                      size={14}
                      className="text-secondary shrink-0"
                    />
                    <span>Dringlichkeit</span>
                    <span className="ml-auto text-secondary">
                      {sortAsc ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </span>
                  </button>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      </div>
      <div className="expiry-body px-4 pb-3 overflow-y-auto flex-1 min-h-0">
        {filtered.map((item, index) => (
          <div
            key={item.name}
            className="border-b border-border/50 last:border-0"
          >
            <button
              type="button"
              className="w-full flex items-center gap-3 py-2 cursor-pointer hover:bg-surface/60 rounded-lg transition-colors text-left"
              onClick={() => toggleExpand(index)}
            >
              <span
                className={`w-2.5 h-2.5 rounded-full shrink-0 ${urgencyDotColor(item.days)}`}
              />
              <span className="text-sm font-medium text-primary truncate flex-1 min-w-0">
                {item.name}
              </span>
              <div className="text-right shrink-0">
                <p
                  className={`text-xs font-semibold ${urgencyTextColor(item.days)}`}
                >
                  {t("dashboard:expiryWarnings.days", { count: item.days })}
                </p>
                <p className="text-xs text-muted">{item.exp}</p>
              </div>
            </button>
            <AnimatePresence>
              {expandedSet.has(index) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={springs.snappy}
                  className="overflow-hidden"
                >
                  <ExpandedExpiryDetail item={item} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}
